// src/pages/AdminNotificationSettings.tsx
import React, { useEffect, useState } from 'react';
import {
	Card,
	CardBody,
	Row,
	Col,
	Input,
	Spinner,
	Button,
	FormGroup,
	Label,
	FormText,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	UncontrolledDropdown,
	Container,
} from 'reactstrap';
import { useSelectStyles } from "Components/Hooks/useSelectStyles";
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { ApplicationState } from 'store';
import { tenantUpdateRequest } from 'store/user/action';
import { userGetRequest } from 'store/auth/action';
import { parse, format } from 'date-fns';
import {
	CATEGORIES,
	CHANNELS,
	getMeta,
	isDigestTemplate,
} from 'helpers/notifications/notificationConfig';

type ChannelKey = string;
type TemplateKey = string;
type Mode = 'custom' | 'daily';

interface ChannelPermission {
	enabled: boolean;
	default: boolean;
}

interface ReminderPermission {
	enabled: boolean;      // admin turns reminders on/off
	overrideable: boolean; // may users override?
	mode: Mode;            // default mode
	offsets: number[];     // default offsets
	digestTime: string;    // default summary time
}

interface TemplateSetting {
	channels: Record<ChannelKey, ChannelPermission>;
	reminder: ReminderPermission;
}

type AdminNotificationSettingsMap = Record<TemplateKey, TemplateSetting>;

const OFFSET_VALUES = [86400, 3600, 900, 600, 300];
const COMMON_DIGEST_TIMES = Array.from({ length: 24 }, (_, i) =>
	String(i).padStart(2, '0') + ':00'
);

function prettyTime(hhmm: string) {
	try {
		return format(parse(hhmm, 'HH:mm', new Date()), 'h:mm aa');
	} catch {
		return hhmm;
	}
}

function formatOffsetLabel(seconds: number): string {
	if (seconds % 86400 === 0) {
		const days = seconds / 86400;
		return `${days} day${days > 1 ? 's' : ''} before`;
	}
	if (seconds % 3600 === 0) {
		const hrs = seconds / 3600;
		return `${hrs} hour${hrs > 1 ? 's' : ''} before`;
	}
	const mins = seconds / 60;
	return `${mins} minute${mins > 1 ? 's' : ''} before`;
}

const AdminNotificationSettings: React.FC = () => {
	const customStyles = useSelectStyles();
	const dispatch = useDispatch<ThunkDispatch<any, null, Action<string>>>();
	const tenant = useSelector(
		(s: ApplicationState) => s.auth.userProfile.tenant
	);
	const [settings, setSettings] = useState<AdminNotificationSettingsMap>(
		() => buildDefault()
	);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	function buildDefault(): AdminNotificationSettingsMap {
		const def = {} as AdminNotificationSettingsMap;
		CATEGORIES.forEach((cat) =>
			cat.templates.forEach((tpl) => {
				const chMap: Record<ChannelKey, ChannelPermission> = {};
				CHANNELS.forEach((ch) => {
					chMap[ch.key] = {
						enabled: ch.defaultEnabled,
						default: ch.defaultEnabled,
					};
				});
				def[tpl.key] = {
					channels: chMap,
					reminder: {
						enabled: false,
						overrideable: false,
						mode: 'custom',
						offsets: [],
						digestTime: '',
					},
				};
			})
		);
		return def;
	}

	function mergeWithRaw(rawJson: string): AdminNotificationSettingsMap {
		const merged = buildDefault();
		let raw: any = {};
		try {
			raw = JSON.parse(rawJson);
		} catch { }
		Object.entries(raw).forEach(([tplKey, cfg]: any) => {
			if (!merged[tplKey]) return;
			if (cfg.channels) {
				Object.entries(cfg.channels).forEach(([chKey, perm]: any) => {
					if (merged[tplKey].channels[chKey]) {
						merged[tplKey].channels[chKey] = perm;
					}
				});
			}
			if (cfg.reminder) {
				const r = merged[tplKey].reminder;
				merged[tplKey].reminder = {
					enabled: cfg.reminder.enabled ?? r.enabled,
					overrideable: cfg.reminder.overrideable ?? r.overrideable,
					mode: cfg.reminder.mode ?? r.mode,
					offsets: cfg.reminder.offsets ?? r.offsets,
					digestTime: cfg.reminder.digestTime ?? r.digestTime,
				};
			}
		});
		return merged;
	}

	useEffect(() => {
		if (tenant?.notificationSettings) {
			setSettings(mergeWithRaw(tenant.notificationSettings));
		} else {
			setSettings(buildDefault());
		}
		setLoading(false);
	}, [tenant]);

	const toggleChannel = (
		tpl: TemplateKey,
		ch: ChannelKey,
		fld: 'enabled' | 'default'
	) => {
		setSettings((s) => ({
			...s,
			[tpl]: {
				...s[tpl],
				channels: {
					...s[tpl].channels,
					[ch]: { ...s[tpl].channels[ch], [fld]: !s[tpl].channels[ch][fld] },
				},
				reminder: s[tpl].reminder,
			},
		}));
	};

	const updateReminderPerm = (
		tpl: TemplateKey,
		rem: Partial<ReminderPermission>
	) => {
		setSettings((s) => ({
			...s,
			[tpl]: {
				...s[tpl],
				reminder: { ...s[tpl].reminder, ...rem },
			},
		}));
	};

	const resetToDefault = () => setSettings(buildDefault());

	const saveChanges = () => {
		setSaving(true);
		dispatch(
			tenantUpdateRequest(
				{
					attributes: [
						{
							key: 'notification_settings',
							value: JSON.stringify(settings),
						},
					],
				},
				() => {
					dispatch(userGetRequest());
					setSaving(false);
				},
				() => setSaving(false)
			)
		);
	};

	function bulkToggleCategoryAdmin(
		categoryKey: string,
		field: 'enabled' | 'default',
		value: boolean,
		settings: AdminNotificationSettingsMap
	): AdminNotificationSettingsMap {
		const updated = { ...settings };
		const category = CATEGORIES.find((c) => c.key === categoryKey);
		if (!category) return updated;

		category.templates.forEach((tpl) => {
			updated[tpl.key] = { ...updated[tpl.key] };
			CHANNELS.forEach((ch) => {
				updated[tpl.key].channels[ch.key][field] = value;
			});
		});

		return updated;
	}

	return (
		<div className="">
				<Card className="border-0 shadow-sm">
					<CardBody className="pb-4">
						<div className="d-flex justify-content-between mb-4">
							<div>
								<h4>
									<i className="ri-shield-keyhole-line me-2" />
									Workspace Notification Controls
								</h4>
								<p className="text-muted small mb-0">
									Manage which notifications users can receive and customize
									reminder defaults.
								</p>
							</div>
							<div className="d-flex gap-2">
								<Button outline onClick={resetToDefault}>
									Reset to Default
								</Button>
								<Button
									color="primary"
									onClick={saveChanges}
									disabled={saving}
								>
									{saving ? 'Saving…' : 'Save Changes'}
								</Button>
							</div>
						</div>

						{loading ? (
							<div className="text-center py-5">
								<Spinner />
							</div>
						) : (
							<div className="d-flex flex-column gap-4">
								{CATEGORIES.map((cat) => {
									const visible = cat.templates.filter(
										(tpl) => !isDigestTemplate(tpl.key)
									);
									return (
										<div
											key={cat.key}
											className="p-3 rounded bg-light-subtle"
										>
											<h6 className="fw-semibold mb-2">{cat.label}</h6>
											<p className="text-muted small mb-3">
												{cat.description}
											</p>

											{visible.map((tpl) => {
												const icon = getMeta(tpl.key)?.icon || '';
												const supportsReminder = getMeta(
													tpl.key
												)?.supportsReminder;
												const cfg = settings[tpl.key];

												return (
													<div
														key={tpl.key}
														className="p-3 rounded shadow-sm mb-3"
													>
														<div className="fw-medium d-flex align-items-center gap-2 mb-3">
															<i className={`${icon} fs-5`} /> {tpl.label}
														</div>

														<Row className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3 mb-3">
															{CHANNELS.map((channel) => {
																const perm =
																	cfg.channels[channel.key];
																return (
																	<Col key={channel.key}>
																		<div className="border rounded p-2 bg-light-subtle h-100">
																			<div className="text-muted small mb-1">
																				{channel.label}
																			</div>
																			<FormGroup switch className="mb-1">
																				<Input
																					type="switch"
																					checked={perm.enabled}
																					onChange={() =>
																						toggleChannel(
																							tpl.key,
																							channel.key,
																							'enabled'
																						)
																					}
																				/>
																				<Label>
																					Allow users to update this
																				</Label>
																			</FormGroup>
																			<FormGroup switch>
																				<Input
																					type="switch"
																					checked={perm.default}
																					disabled={!perm.enabled}
																					onChange={() =>
																						toggleChannel(
																							tpl.key,
																							channel.key,
																							'default'
																						)
																					}
																				/>
																				<Label>
																				{perm.default ? 'Set to ON by default' : 'Set to OFF by default'}
																				</Label>
																			</FormGroup>
																		</div>
																	</Col>
																);
															})}
														</Row>

														{supportsReminder && (
															<div className="border rounded p-3 bg-light-subtle">
																<FormGroup switch>
																	<Input
																		type="switch"
																		checked={cfg.reminder.enabled}
																		onChange={() =>
																			updateReminderPerm(tpl.key, {
																				enabled: !cfg.reminder.enabled,
																			})
																		}
																	/>
																	<Label>
																		Turn on event reminders
																	</Label>
																</FormGroup>
																<FormText color="muted">
																	Users will get reminders for upcoming
																	events when enabled.
																</FormText>

																<FormGroup switch className="mt-3">
																	<Input
																		type="switch"
																		checked={cfg.reminder.overrideable}
																		onChange={() =>
																			updateReminderPerm(tpl.key, {
																				overrideable:
																					!cfg.reminder.overrideable,
																			})
																		}
																	/>
																	<Label>
																		Let users customize reminders
																	</Label>
																</FormGroup>
																<FormText color="muted">
																	When off, users see exactly these reminder
																	settings and cannot change them.
																</FormText>

																{cfg.reminder.enabled && (
																	<>
																		<FormGroup className="mt-3">
																			<Label className="small">
																				Reminder style
																			</Label>
																			<UncontrolledDropdown>
																				<DropdownToggle
																					tag="button"
																					className="btn bg-dark-subtle w-100 d-flex align-items-center justify-content-between"
																				>
																					{cfg.reminder.mode ===
																						'custom'
																						? 'Multiple reminders'
																						: 'Daily summary'}
																					<i className="ri-arrow-down-s-line ms-auto" />
																				</DropdownToggle>
																				<DropdownMenu>
																					<DropdownItem
																						active={
																							cfg.reminder.mode ===
																							'custom'
																						}
																						onClick={() =>
																							updateReminderPerm(tpl.key, {
																								mode: 'custom',
																							})
																						}
																					>
																						Multiple reminders
																					</DropdownItem>
																					<DropdownItem
																						active={cfg.reminder.mode ===
																							'daily'}
																						onClick={() =>
																							updateReminderPerm(tpl.key, {
																								mode: 'daily',
																							})
																						}
																					>
																						Daily summary
																					</DropdownItem>
																				</DropdownMenu>
																			</UncontrolledDropdown>
																		</FormGroup>
																		<FormText color="muted">
																			Choose if users get several reminders
																			or one daily digest.
																		</FormText>

																		{cfg.reminder.mode === 'custom' ? (
																			<div className="mt-2">
																				<Label className="small">
																					Reminder times
																				</Label>
																				<Select
																					isMulti
																					options={OFFSET_VALUES.map((v) => ({
																						value: v,
																						label: formatOffsetLabel(v),
																					}))}
																					value={cfg.reminder.offsets.map(
																						(o) => ({
																							value: o,
																							label: formatOffsetLabel(o),
																						})
																					)}
																					onChange={(
																						opts: any[]
																					) =>
																						updateReminderPerm(tpl.key, {
																							offsets: opts.map(
																								(x) => x.value
																							),
																						})
																					}
																					customStyles={customStyles}
																				/>
																				<FormText color="muted">
																					Select when reminders should be sent
																					relative to the event start.
																				</FormText>
																			</div>
																		) : (
																			<FormGroup className="mt-2">
																				<Label className="small">
																					Daily summary at
																				</Label>
																				<Select
																					isSearchable
																					options={COMMON_DIGEST_TIMES.map(
																						(time) => ({
																							value: time,
																							label: prettyTime(time),
																						})
																					)}
																					value={
																						cfg.reminder.digestTime
																							? {
																								value:
																									cfg.reminder.digestTime,
																								label: prettyTime(
																									cfg.reminder.digestTime
																								),
																							}
																							: null
																					}
																					onChange={(opt: any) =>
																						updateReminderPerm(tpl.key, {
																							digestTime: opt.value,
																						})
																					}
																					placeholder="Select time…"
																					classNamePrefix="react-select"
																					customStyles={customStyles}
																				/>
																				<FormText color="muted">
																					Pick what time you'd like the daily
																					summary delivered.
																				</FormText>
																			</FormGroup>
																		)}
																	</>
																)}
															</div>
														)}
													</div>
												);
											})}
										</div>
									);
								})}
							</div>
						)}
					</CardBody>

					<div className="d-flex justify-content-end gap-2 p-3 border-top">
						<Button
							color="primary"
							onClick={saveChanges}
							disabled={saving}
						>
							{saving ? 'Saving…' : 'Save Changes'}
						</Button>
					</div>
				</Card>
		</div>
	);
};

export default AdminNotificationSettings;
